import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import Input from '../../components/input/Input';
import { useClientTheme } from '../../context/ThemeContext';

import { amount, applicationData, clientData } from '../../types/interfaces';
import { CURRENCY_CODES } from '../../utils/Constants';
import { formatCurrency } from '../../utils/Masks';
import SummaryApplication from '../Origination/Summary/Application';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

const ResponsiveContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const TableContainer = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryContainer = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: 100%;
  }
`;

const SummaryCardWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  word-wrap: break-word;
  white-space: normal;
  gap: 8px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const InputRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  
  & > h3 {
    min-width: 120px;
    white-space: nowrap;
  }
  
  & input {
    background-color: transparent !important;
  }
`;



interface ISummaryAmountsProps {
  clientData: clientData;
  isManualAmount: boolean;
  amounts: Array<amount>;
  products: Array<applicationData>;
  hideFianzaPagare?: boolean;
  editable?: boolean;
  onNoteAmountChange?: (value: number) => void;
  onGuaranteeAmountChange?: (value: number) => void;
}
const SummaryAmounts = ({
  clientData,
  isManualAmount,
  amounts,
  products,
  hideFianzaPagare = false,
  editable = false,
  onNoteAmountChange,
  onGuaranteeAmountChange
}: ISummaryAmountsProps) => {
  const location = useLocation();
  const { clientTheme } = useClientTheme();
  const isInstrumentationPath = location.pathname.includes('/instrumentacion');

  const totals = useMemo(() => {
    let totalArs = 0;
    let totalUsd = 0;
    products.forEach((product: applicationData) => {
      const value = Number(product.amount.value);
      if (product.amount.currency_code === CURRENCY_CODES.ars || product.amount.currency_code === 'ARS') {
        totalArs += value;
      } else if (product.amount.currency_code === CURRENCY_CODES.usd || product.amount.currency_code === 'USD') {
        totalUsd += value;
      }
    });
    return { totalArs, totalUsd };
  }, [products]);

  const renderCurrencyAmount = (
    currencyCode: string,
    requestedAmount: Array<amount>,
    totalAmount: number | string
  ) => {
    const currencyData = requestedAmount.find(
      (item) => item.currency_code === currencyCode
    );
    const amount = currencyData ? currencyData.value : totalAmount;
    return formatCurrency(amount.toString());
  };

  return (
    <ResponsiveContainer>
      <TableContainer>
        <SummaryApplication applications={products}></SummaryApplication>
      </TableContainer>
      <SummaryContainer>
        <Card 
          border={true} 
          variant={clientTheme ? 'primary' : 'success'} 
          modifier='weak' 
          spacing={'1'}
          width='100%'
          height='100%'
        >
          <SummaryCardWrapper>
            <ColumnContainer>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
                align='center'
              >
                {CURRENCY_CODES.ars}{' '}
                {isManualAmount
                  ? renderCurrencyAmount(
                    CURRENCY_CODES.ars,
                    amounts,
                    totals.totalArs
                  )
                  : formatCurrency(totals.totalArs)}
              </H>
              <H
                type='2'
                withMargin={false}
                weight='bold'
                variant={clientTheme ? 'primary' : 'success'}
                align='center'
              >
                {CURRENCY_CODES.usd}{' '}
                {isManualAmount
                  ? renderCurrencyAmount(
                    CURRENCY_CODES.usd,
                    amounts,
                    totals.totalUsd
                  )
                  : formatCurrency(totals.totalUsd)}
              </H>
            </ColumnContainer>

            {isInstrumentationPath && !hideFianzaPagare && (
              <ColumnContainer>
                {editable ? (
                  <>
                    <InputRowContainer>
                      <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bold' withMargin={false}>
                        Valor fianza
                      </H>
                      <Input
                        type='text'
                        value={formatCurrency(clientData.note_amount || '0').toString()}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          onNoteAmountChange?.(Number(numericValue));
                        }}
                      />
                    </InputRowContainer>
                    <InputRowContainer>
                      <H type='3' variant={clientTheme ? 'primary' : 'success'} weight='bold' withMargin={false}>
                        Valor pagaré
                      </H>
                      <Input
                        type='text'
                        value={formatCurrency(clientData.guarantee_amount || '0').toString()}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          onGuaranteeAmountChange?.(Number(numericValue));
                        }}
                      />
                    </InputRowContainer>
                  </>
                ) : (
                  <>
                    <H
                      type='2'
                      withMargin={false}
                      weight='bold'
                      variant={clientTheme ? 'primary' : 'success'}
                      align='center'
                    >
                      Valor fianza {formatCurrency(clientData.note_amount || '0')}
                    </H>
                    <H
                      type='2'
                      withMargin={false}
                      weight='bold'
                      variant={clientTheme ? 'primary' : 'success'}
                      align='center'
                    >
                      Valor pagaré {formatCurrency(clientData.guarantee_amount || '0')}
                    </H>
                  </>
                )}
              </ColumnContainer>
            )}
          </SummaryCardWrapper>
        </Card>
      </SummaryContainer>
    </ResponsiveContainer>
  );
};
export default SummaryAmounts;

